<script setup lang="ts">
import { storeToRefs } from 'pinia';
import MenuList from '@app/layout/nav/desktop/components/MenuList.vue';
import { trans, transchoice } from '@tools/translation.ts';
import { getDistrict } from '@tools/address-utils.ts';
import LocationSubmenuItem from '@app/layout/nav/LocationSubmenuItem.vue';
import LocationHeaderLink from '@app/layout/nav/LocationHeaderLink.vue';
import { slugify } from '@tools/stringFormatter.ts';
import Divider from '@components/Divider.vue';
import { useNavbarStore } from '@stores/layout/useNavbarStore';
import Typography from '@components/Typography.vue';

const navbarStore = useNavbarStore();
const { formatParisPoiSlug } = navbarStore;
const {
  parisAreaUrl,
  parisDistricts,
  parisPois,
  idfAreaUrl,
  idfCities,
  franceAreaUrl,
  franceCities,
  plusCoworkingParisUrl,
  plusPriceParisUrl,
} = storeToRefs(navbarStore);
</script>

<template>
  <MenuList
    :class="[
      'px-4',
      'flex flex-col gap-y-4'
    ]"
  >
    <li>
      <LocationHeaderLink
        :link="parisAreaUrl"
        class="h-14 w-full"
      >
        {{ trans('user_menu.top.locations.paris.title') }}
      </LocationHeaderLink>

      <MenuList class="grid grid-cols-4">
        <li
          v-for="parisDistrict in parisDistricts"
          :key="parisDistrict"
        >
          <LocationSubmenuItem
            class="h-11 justify-center"
            :link="`location-bureau-paris-${parisDistrict}`"
          >
            {{ transchoice('user_menu.top.locations.paris.district', { district: getDistrict(parisDistrict) }) }}
          </LocationSubmenuItem>
        </li>
      </MenuList>

      <Divider class="my-3" color="gray" />

      <MenuList>
        <li
          v-for="parisPoi in parisPois"
          :key="parisPoi"
        >
          <LocationSubmenuItem
            :link="`location-bureau-${slugify(formatParisPoiSlug(parisPoi))}`"
            class="h-11"
          >
            {{ parisPoi }}
          </LocationSubmenuItem>
        </li>
      </MenuList>
    </li>

    <li>
      <Divider class="m-0" color="gray" />
    </li>

    <li>
      <LocationHeaderLink
        :link="idfAreaUrl"
        class="h-14 w-full"
      >
        {{ trans('user_menu.top.locations.ile_de_france.title') }}
      </LocationHeaderLink>

      <MenuList>
        <li
          v-for="idfCity in idfCities"
          :key="idfCity"
        >
          <LocationSubmenuItem
            :link="`location-bureau-${slugify(idfCity)}`"
            class="h-11"
          >
            {{ idfCity }}
          </LocationSubmenuItem>
        </li>
      </MenuList>
    </li>

    <li>
      <Divider class="m-0" color="gray" />
    </li>

    <li>
      <LocationHeaderLink
        :link="franceAreaUrl"
        class="h-14 w-full"
      >
        {{ trans('user_menu.top.locations.france.title') }}
      </LocationHeaderLink>

      <MenuList class="grid grid-cols-2">
        <li
          v-for="franceCity in franceCities"
          :key="franceCity"
        >
          <LocationSubmenuItem
            :link="`location-bureau-${slugify(franceCity)}`"
            class="h-11"
          >
            {{ franceCity }}
          </LocationSubmenuItem>
        </li>
      </MenuList>
    </li>

    <li>
      <Divider class="m-0" color="gray" />
    </li>

    <li>
      <Typography
        variant="body-bold"
        class="flex h-14 w-full items-center p-2"
      >
        {{ trans('user_menu.plus') }}
      </Typography>

      <MenuList>
        <li>
          <LocationSubmenuItem :link="plusCoworkingParisUrl">
            {{ trans('user_menu.top.locations.paris.coworking') }}
          </LocationSubmenuItem>
        </li>

        <li>
          <LocationSubmenuItem :link="plusPriceParisUrl">
            {{ trans('user_menu.top.locations.paris.office_price') }}
          </LocationSubmenuItem>
        </li>
      </MenuList>
    </li>
  </MenuList>
</template>
